import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../hooks/useToast'
import type { DeveloperResponse, UserSessionResponse } from '../../interfaces'
import { DeveloperErrorResponse } from '../../interfaces/DeveloperResponse'
import PasswordInput from '../PasswordInput'
import SetPasswordAlert from '../SetPasswordAlert'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  request: Req<DeveloperResponse>
  setData: React.Dispatch<React.SetStateAction<DeveloperResponse | undefined>>
  onClose(): void
}

interface FormProps {
  email: string
  password: string
}

const RevealApiKeyModal = ({
  isOpen,
  onClose,
  user,
  request,
  setData,
}: Props): React.ReactElement => {
  const toast = useToast()
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ email: user?.userEmail ?? '', password: '' }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              const response = (await request.post(
                `/api/clients/${user.clientNumber}/settings/developer`,
                { userEmail: values.email, userPassword: values.password },
              )) as unknown as DeveloperResponse | DeveloperErrorResponse
              if (!request.error) {
                setData(response as DeveloperResponse)
                onClose()
              } else {
                setFormError(
                  (response as DeveloperErrorResponse)?.message ??
                    t(
                      'invalid_account_credentials_entered',
                      'You have entered invalid account credentials.',
                    ),
                )
              }
            }
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string().required('Required'),
            password: Yup.string().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>
                {t('reveal_api_keys', 'Reveal API keys')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!user?.isPasswordSetByUser ? <SetPasswordAlert /> : null}
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4} alignItems="flex-start">
                  <ChakraText>
                    {t(
                      'reveal_api_keys_description',
                      'To continue, please enter your account password.',
                    )}
                  </ChakraText>
                  <Field name="email">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.email && form.touched.email,
                        )}
                        isReadOnly
                        isRequired
                      >
                        <FormLabel>{t('email', 'Email')}</FormLabel>
                        <Input {...field} placeholder="Email" />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.password && form.touched.password,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('password', 'Password')}</FormLabel>
                        <PasswordInput
                          field={field}
                          isDisabled={!user?.isPasswordSetByUser}
                          isPasswordSetByUser={
                            user?.isPasswordSetByUser ?? false
                          }
                          showToggle={true}
                        />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RevealApiKeyModal
